import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar, faRobot, faChartLine, faCogs, faArrowRight, faIndustry,
  faBolt, faShieldHalved, faGaugeHigh, faPlus, faMinus
} from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useCountUp from '../hooks/useCountUp';
import ClientLogos from '../components/ClientLogos';
import CaseStudies from '../components/CaseStudies';
import ROICalculator from '../components/ROICalculator';
import { useLanguage } from '../i18n/LanguageContext';
import './HomePage.css';

const faqItemsByLang = {
  fr: [
    { q: "Combien de temps dure un projet d'automatisation ?", a: "La durée dépend de la complexité des processus. En général, nos clients voient des résultats concrets entre 2 et 8 semaines. Un premier audit gratuit nous permet de vous donner une estimation précise." },
    { q: "Faut-il remplacer nos outils existants ?", a: "Non. Nos solutions s'intègrent à votre écosystème actuel (ERP, CRM, comptabilité). Nous connectons vos outils plutôt que de les remplacer, ce qui réduit les coûts et accélère l'adoption." },
    { q: "Qu'est-ce que NIROPROD exactement ?", a: "NIROPROD est notre ERP propriétaire conçu pour les PME industrielles. Il gère la production, la facturation, les stocks et le suivi client depuis une seule plateforme, avec des iPads pour le suivi en atelier." },
    { q: "Qui est TECHMILL ?", a: "TECHMILL est la branche sous-traitance industrielle d'AI Dynamics. Nous proposons usinage CNC, soudure, traitement de surface et assemblage de pièces mécaniques, avec un suivi digitalisé complet." },
    { q: "Comment se passe le support après le déploiement ?", a: "Vous bénéficiez d'un interlocuteur dédié, d'un support réactif et d'évolutions continues. Nous ne vous livrons pas un produit puis disparaissons : nous accompagnons votre évolution sur le long terme." },
    { q: "Vos solutions sont-elles sécurisées ?", a: "Oui. Hébergement sécurisé, gestion des droits utilisateurs, audit trail complet, chiffrement des données sensibles. Nous respectons les standards RGPD." }
  ],
  en: [
    { q: "How long does an automation project take?", a: "Duration depends on process complexity. Our clients typically see concrete results between 2 and 8 weeks. A free initial audit allows us to provide a precise estimate." },
    { q: "Do we have to replace our existing tools?", a: "No. Our solutions integrate with your current ecosystem (ERP, CRM, accounting). We connect your tools rather than replacing them, which reduces costs and accelerates adoption." },
    { q: "What exactly is NIROPROD?", a: "NIROPROD is our proprietary ERP designed for industrial SMEs. It manages production, invoicing, inventory and customer tracking from a single platform, with iPads for workshop tracking." },
    { q: "Who is TECHMILL?", a: "TECHMILL is the industrial subcontracting branch of AI Dynamics. We offer CNC machining, welding, surface treatment and assembly of mechanical parts, with full digital tracking." },
    { q: "How does support work after deployment?", a: "You benefit from a dedicated contact, responsive support and continuous updates. We don't just deliver a product and disappear: we support your evolution long-term." },
    { q: "Are your solutions secure?", a: "Yes. Secure hosting, user rights management, full audit trail, encryption of sensitive data. We comply with GDPR standards." }
  ],
  pl: [
    { q: "Ile trwa projekt automatyzacji?", a: "Czas trwania zależy od złożoności procesów. Zazwyczaj nasi klienci widzą konkretne rezultaty między 2 a 8 tygodniem. Bezpłatny audyt wstępny pozwala nam podać precyzyjną wycenę." },
    { q: "Czy trzeba wymienić istniejące narzędzia?", a: "Nie. Nasze rozwiązania integrują się z obecnym ekosystemem (ERP, CRM, księgowość). Łączymy Twoje narzędzia zamiast je wymieniać, co redukuje koszty i przyspiesza wdrożenie." },
    { q: "Czym dokładnie jest NIROPROD?", a: "NIROPROD to nasz autorski ERP zaprojektowany dla przemysłowych MŚP. Zarządza produkcją, fakturowaniem, zapasami i obsługą klienta z jednej platformy, z iPadami do śledzenia w warsztacie." },
    { q: "Kim jest TECHMILL?", a: "TECHMILL to oddział podwykonawstwa przemysłowego AI Dynamics. Oferujemy obróbkę CNC, spawanie, obróbkę powierzchniową i montaż części mechanicznych, z pełnym śledzeniem cyfrowym." },
    { q: "Jak wygląda wsparcie po wdrożeniu?", a: "Masz dedykowaną osobę kontaktową, responsywne wsparcie i ciągłe aktualizacje. Nie dostarczamy produktu i nie znikamy: wspieramy Twój rozwój długoterminowo." },
    { q: "Czy Wasze rozwiązania są bezpieczne?", a: "Tak. Bezpieczny hosting, zarządzanie prawami użytkowników, pełny audyt, szyfrowanie wrażliwych danych. Przestrzegamy standardów RODO." }
  ]
};

const HomeFAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const { language, t } = useLanguage();
  const faqItems = faqItemsByLang[language] || faqItemsByLang.fr;

  return (
    <section className="section section-darker home-faq">
      <div className="grid-pattern"></div>
      <div className="container">
        <div className="fade-in" style={{ textAlign: 'center', marginBottom: 56 }}>
          <span className="section-label">{t('faq.label')}</span>
          <h2 className="section-title">
            {t('faq.titleStart')} <span className="gradient-text">{t('faq.titleHighlight')}</span>
          </h2>
          <p className="section-subtitle" style={{ margin: '0 auto' }}>
            {t('faq.subtitle')}
          </p>
        </div>
        <div className="home-faq__list">
          {faqItems.map((item, i) => (
            <div
              key={i}
              className={`home-faq__item ${openIdx === i ? 'home-faq__item--open' : ''}`}
            >
              <button type="button" className="home-faq__question" onClick={() => setOpenIdx(openIdx === i ? -1 : i)}>
                <span>{item.q}</span>
                <FontAwesomeIcon icon={openIdx === i ? faMinus : faPlus} className="home-faq__icon" />
              </button>
              <div className="home-faq__answer">
                <p>{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AnimatedStatsBar = () => {
  const satisfaction = useCountUp(98, 1800);
  const processus = useCountUp(50, 1800);
  const productivite = useCountUp(3, 1800);
  const { t } = useLanguage();

  return (
    <div className="hero__stats" ref={satisfaction.ref}>
      <div className="hero__stat">
        <span className="hero__stat-number" ref={satisfaction.ref}>{satisfaction.count}%</span>
        <span className="hero__stat-label">{t('hero.statSatisfaction')}</span>
      </div>
      <div className="hero__stat-divider"></div>
      <div className="hero__stat">
        <span className="hero__stat-number" ref={processus.ref}>{processus.count}+</span>
        <span className="hero__stat-label">{t('hero.statProcesses')}</span>
      </div>
      <div className="hero__stat-divider"></div>
      <div className="hero__stat">
        <span className="hero__stat-number" ref={productivite.ref}>{productivite.count}x</span>
        <span className="hero__stat-label">{t('hero.statProductivity')}</span>
      </div>
    </div>
  );
};

const HomePage = () => {
  useScrollAnimation();
  const { t } = useLanguage();

  return (
    <main className="home">
      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="hero__image" style={{ backgroundImage: 'url(/images/hero-original.jpg)' }}></div>
        <div className="hero__animated-bg"></div>
        <div className="grid-pattern"></div>
        <div className="hero__glow hero__glow--1"></div>
        <div className="hero__glow hero__glow--2"></div>

        <div className="hero__content container">
          <div className="hero__badge">
            <span className="hero__badge-dot"></span>
            {t('hero.badge')}
          </div>
          <h1 className="hero__title">
            {t('hero.titleStart')}{' '}
            <span className="gradient-text">{t('hero.titleHighlight')}</span>{' '}
            {t('hero.titleEnd')}
          </h1>
          <p className="hero__subtitle">
            {t('hero.subtitle')}
          </p>
          <div className="hero__stars">
            {[...Array(5)].map((_, i) => (
              <FontAwesomeIcon key={i} icon={faStar} />
            ))}
            <span className="hero__rating-text">{t('hero.ratingText')}</span>
          </div>
          <div className="hero__actions">
            <Link to="/contact" className="btn btn-primary">
              {t('hero.ctaPrimary')} <FontAwesomeIcon icon={faArrowRight} />
            </Link>
            <Link to="/services" className="btn btn-outline">
              {t('hero.ctaSecondary')}
            </Link>
          </div>

          {/* Stats bar */}
          <AnimatedStatsBar />
        </div>
      </section>

      {/* ===== CLIENT LOGOS ===== */}
      <ClientLogos />

      {/* ===== BENTO SERVICES ===== */}
      <section className="section section-dark home-services">
        <div className="dot-pattern"></div>
        <div className="container">
          <div className="fade-in" style={{ textAlign: 'center' }}>
            <span className="section-label">{t('homeServices.label')}</span>
            <h2 className="section-title">
              {t('homeServices.titleStart')} <span className="gradient-text">{t('homeServices.titleHighlight')}</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto 64px' }}>
              {t('homeServices.subtitle')}
            </p>
          </div>

          <div className="bento-grid">
            <div className="glass-card bento-large fade-in">
              <img src="/images/home-automation.jpg" alt={t('homeServices.automationTitle')} className="bento-img" loading="lazy" />
              <div className="bento-icon"><FontAwesomeIcon icon={faCogs} /></div>
              <h3>{t('homeServices.automationTitle')}</h3>
              <p>{t('homeServices.automationDesc')}</p>
              <Link to="/services" className="bento-link">
                {t('homeServices.learnMore')} <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>

            <div className="glass-card fade-in fade-in-delay-1">
              <img src="/images/home-analytics.jpg" alt={t('homeServices.aiTitle')} className="bento-img" loading="lazy" />
              <div className="bento-icon"><FontAwesomeIcon icon={faChartLine} /></div>
              <h3>{t('homeServices.aiTitle')}</h3>
              <p>{t('homeServices.aiDesc')}</p>
              <Link to="/services" className="bento-link">
                {t('homeServices.explore')} <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>

            <div className="glass-card fade-in fade-in-delay-2">
              <img src="/images/niroprod-5.png" alt="NIROPROD" className="bento-img" loading="lazy" />
              <div className="bento-icon bento-icon--accent"><FontAwesomeIcon icon={faRobot} /></div>
              <h3>{t('homeServices.niroprodTitle')}</h3>
              <p>{t('homeServices.niroprodDesc')}</p>
              <Link to="/niroprod" className="bento-link">
                {t('homeServices.discover')} <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>

            <div className="glass-card bento-wide bento-wide--with-img fade-in fade-in-delay-1">
              <img src="/images/tm-milling.jpg" alt="TECHMILL" className="bento-wide-img" loading="lazy" />
              <div className="bento-wide__overlay">
                <div className="bento-icon bento-icon--techmill"><FontAwesomeIcon icon={faIndustry} /></div>
                <div className="bento-wide__content">
                  <div>
                    <h3>{t('homeServices.techmillTitle')}</h3>
                    <p>{t('homeServices.techmillDesc')}</p>
                  </div>
                  <Link to="/techmill" className="btn btn-outline" style={{ flexShrink: 0 }}>
                    {t('homeServices.discoverTechmill')} <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHY US STRIP ===== */}
      <section className="section section-darker home-why">
        <div className="grid-pattern"></div>
        <div className="container">
          <div className="home-why__grid">
            <div className="home-why__item fade-in">
              <div className="home-why__icon"><FontAwesomeIcon icon={faBolt} /></div>
              <h4>{t('homeWhy.fastTitle')}</h4>
              <p>{t('homeWhy.fastDesc')}</p>
            </div>
            <div className="home-why__item fade-in fade-in-delay-1">
              <div className="home-why__icon"><FontAwesomeIcon icon={faShieldHalved} /></div>
              <h4>{t('homeWhy.reliabilityTitle')}</h4>
              <p>{t('homeWhy.reliabilityDesc')}</p>
            </div>
            <div className="home-why__item fade-in fade-in-delay-2">
              <div className="home-why__icon"><FontAwesomeIcon icon={faGaugeHigh} /></div>
              <h4>{t('homeWhy.performanceTitle')}</h4>
              <p>{t('homeWhy.performanceDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CASE STUDIES ===== */}
      <CaseStudies />

      {/* ===== TESTIMONIAL ===== */}
      <section className="section section-dark home-testimonial">
        <div className="grid-pattern"></div>
        <div className="home-testimonial__glow"></div>
        <div className="container">
          <div className="home-testimonial__inner fade-in">
            <span className="section-label">{t('testimonial.label')}</span>
            <div className="home-testimonial__stars">
              {[...Array(5)].map((_, i) => (
                <FontAwesomeIcon key={i} icon={faStar} />
              ))}
            </div>
            <blockquote className="home-testimonial__quote">
              {t('testimonial.quote')}
            </blockquote>
            <div className="home-testimonial__author">
              <img src="/images/testimonial-avatar.jpg" alt="KUBITECH" className="home-testimonial__avatar-img" loading="lazy" />
              <div>
                <strong>KUBITECH / P-S-M</strong>
                <span>{t('testimonial.author')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ROI CALCULATOR ===== */}
      <ROICalculator />

      {/* ===== FAQ ===== */}
      <HomeFAQ />

      {/* ===== CTA ===== */}
      <section className="section home-cta">
        <div className="home-cta__bg"></div>
        <div className="container home-cta__inner fade-in">
          <h2 className="section-title">
            {t('cta.titleStart')} <span className="gradient-text">{t('cta.titleHighlight')}</span> {t('cta.titleEnd')}
          </h2>
          <p className="section-subtitle" style={{ margin: '0 auto 48px', textAlign: 'center' }}>
            {t('cta.subtitle')}
          </p>
          <div className="home-cta__actions">
            <Link to="/contact" className="btn btn-primary">
              {t('cta.contact')} <FontAwesomeIcon icon={faArrowRight} />
            </Link>
            <Link to="/services" className="btn btn-outline">
              {t('cta.services')}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
